import { useState } from 'react'
import { motion } from 'framer-motion'
import { Building2, Info, Loader2, PartyPopper } from 'lucide-react'
import { useMyInvitations, useDeclineInvitation } from '@/entities/invitation'
import { InvitationCard } from '@/features/invitations/InvitationCard'
import { CreateOrgFeature } from '@/features/onboarding/CreateOrgFeature'
import { CredentialsManager } from '@/features/onboarding/CredentialsManager'
import { AccountPicker } from '@/features/auth/AccountPicker'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useAuthStore } from '@/shared/state/authstore'
import { Button } from '@/shared/components/ui/button'
import type { RevokedAccount } from '@/entities/session/hooks/useRevokedAccounts'
import {
  ACCESS_PAUSED_CONTACT,
  accessEndedContact,
  accessEndedHeadline,
  accessPausedHeadline,
} from '@/shared/api/employmentRevoked'

type AccountlessHomeProps = {
  /** Organisations the person's access to has ended. Named here instead of left as a greyed-out row. */
  revokedAccounts?: RevokedAccount[]
}

function AccessLostNotice({
  accounts,
  paused,
  first,
}: {
  accounts: RevokedAccount[]
  paused: boolean
  first: boolean
}) {
  const headingClass = `${first ? 'mt-5' : 'mt-8'} text-3xl font-extrabold tracking-tight text-ink`
  if (accounts.length === 1) {
    const name = accounts[0].accountName
    return (
      <>
        <h1 className={headingClass}>{paused ? accessPausedHeadline(name) : accessEndedHeadline(name)}</h1>
        <p className="mt-3 text-[15px] text-ink-3">{paused ? ACCESS_PAUSED_CONTACT : accessEndedContact(name)}</p>
      </>
    )
  }
  return (
    <>
      <h1 className={headingClass}>
        {paused ? 'Your access to these organizations has been paused' : 'Your access to these organizations has ended'}
      </h1>
      <ul className="mt-3 list-disc pl-5 text-[15px] text-ink-3">
        {accounts.map((account) => (
          <li key={account.accountId}>{account.accountName || 'An organization'}</li>
        ))}
      </ul>
      <p className="mt-3 text-[15px] text-ink-3">
        {paused
          ? ACCESS_PAUSED_CONTACT
          : 'If you have questions, contact an administrator at the organization concerned.'}
      </p>
    </>
  )
}

export function AccountlessHome({ revokedAccounts = [] }: AccountlessHomeProps) {
  const { data, isLoading } = useMyInvitations()
  const declineMutation = useDeclineInvitation()
  const availableAccounts = useAuthStore((s) => s.availableAccounts)
  const { selectAccount } = useAuth()
  const [showCreateOrg, setShowCreateOrg] = useState(false)

  const invitations = data?.items ?? []
  const hasInvitations = invitations.length > 0
  const revokedIds = new Set(revokedAccounts.map((a) => a.accountId))
  const pickableAccounts = availableAccounts.filter((a) => !revokedIds.has(a.accountId))
  const hasAccounts = pickableAccounts.length > 0
  const hasRevoked = revokedAccounts.length > 0
  const endedAccounts = revokedAccounts.filter((a) => !a.paused)
  const pausedAccounts = revokedAccounts.filter((a) => a.paused)

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-ink-3" />
      </div>
    )
  }

  return (
    <div className="mx-auto flex min-h-[calc(100vh-52px)] w-full max-w-lg flex-col justify-center px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {hasRevoked && (
          <section className={hasAccounts || hasInvitations ? 'mb-8 border-b border-line pb-6' : ''}>
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
              <Info className="h-7 w-7" />
            </span>
            {endedAccounts.length > 0 && <AccessLostNotice accounts={endedAccounts} paused={false} first />}
            {pausedAccounts.length > 0 && (
              <AccessLostNotice accounts={pausedAccounts} paused first={endedAccounts.length === 0} />
            )}
            {hasAccounts && (
              <p className="mt-3 text-[15px] text-ink-3">Your other organizations are not affected.</p>
            )}
          </section>
        )}

        {hasAccounts && (
          <section>
            <p className="text-sm font-semibold uppercase tracking-wider text-indigo-600">
              Your organizations
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink">
              Choose an organization
            </h1>
            <p className="mt-3 text-[15px] text-ink-3">Select one to continue.</p>
            <div className="mt-6">
              <AccountPicker
                accounts={pickableAccounts}
                onSelect={(account) => selectAccount(account.accountId)}
              />
            </div>
          </section>
        )}

        {hasInvitations && (
          <section className={hasAccounts ? 'mt-8 border-t border-line pt-6' : ''}>
            <p className="text-sm font-semibold uppercase tracking-wider text-indigo-600">
              Invitations
            </p>
            {!hasAccounts && (
              <>
                <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink">
                  You've been invited
                </h1>
                <p className="mt-3 text-[15px] text-ink-3">Join your team in one tap.</p>
              </>
            )}
            <div className="mt-4 space-y-3">
              {invitations.map((invitation) => (
                <InvitationCard
                  key={invitation.id}
                  invitation={invitation}
                  onDecline={declineMutation.mutate}
                  isPending={declineMutation.isPending}
                />
              ))}
            </div>
          </section>
        )}

        {!hasAccounts && !hasInvitations && !hasRevoked && (
          <section>
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
              <PartyPopper className="h-7 w-7" />
            </span>
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-ink">
              You're all set
            </h1>
            <p className="mt-3 text-[15px] text-ink-3">
              We'll bring you in the moment your team invites you — or start your own
              organization below.
            </p>
          </section>
        )}

        <div className="mt-8 border-t border-line pt-6">
          {showCreateOrg ? (
            <CreateOrgFeature embedded />
          ) : (
            <div className="flex flex-col items-start gap-3">
              <Button
                variant="outline"
                className="h-12 rounded-xl border-line px-5 text-[15px] font-medium hover:bg-hover"
                onClick={() => setShowCreateOrg(true)}
              >
                <Building2 className="mr-2 h-5 w-5" />
                Create your organization
              </Button>
            </div>
          )}
        </div>

        <CredentialsManager />
      </motion.div>
    </div>
  )
}
